import { FilmStrip, FolderSimple, type Icon, MagicWand, SignOut, UserCircle } from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { clsx } from 'clsx';
import { type ReactNode, useEffect } from 'react';
import { Link, NavLink, Outlet, useLocation, useNavigate } from 'react-router';
import { signOut } from '../lib/auth-client.ts';
import { useMe } from '../lib/queries.ts';
import { Logo } from './brand.tsx';

/** Quatre entrées, en mots simples. Les outils se choisissent depuis « Créer ». */
const NAV: { to: string; label: string; short: string; icon: Icon; end?: boolean }[] = [
  { to: '/', label: 'Créer', short: 'Créer', icon: MagicWand, end: true },
  { to: '/projets', label: 'Mes vidéos', short: 'Vidéos', icon: FilmStrip },
  { to: '/bibliotheque', label: 'Mes fichiers', short: 'Fichiers', icon: FolderSimple },
  { to: '/reglages', label: 'Mon compte', short: 'Compte', icon: UserCircle },
];

export function useLogout() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  return async () => {
    await signOut();
    qc.clear();
    navigate('/');
  };
}

/** Crédits restants et jauge du mois. */
function CreditsCard() {
  const { data: me } = useMe();
  if (!me) return <div className="h-[92px] animate-pulse rounded-lg bg-surface-2" />;
  const { balance, monthlyAllowance } = me.credits;
  const ratio = monthlyAllowance > 0 ? Math.min(1, balance / monthlyAllowance) : 0;
  return (
    <div className="clay flex flex-col gap-2.5 p-4">
      <p className="flex items-baseline justify-between gap-2">
        <span className="text-sm font-bold text-ink-2">Crédits</span>
        <span className="text-xl font-extrabold text-ink tabular-nums">{balance}</span>
      </p>
      <div className="clay-inset h-2 overflow-hidden rounded-full">
        <div
          className="h-full rounded-full [background-image:var(--gradient-primary)]"
          style={{ width: `${Math.max(4, ratio * 100)}%` }}
        />
      </div>
      <p className="text-xs text-ink-3">Rechargés le 1er du mois</p>
    </div>
  );
}

function SideNav() {
  const logout = useLogout();
  const { data: me } = useMe();
  return (
    <aside className="sticky top-0 hidden h-dvh flex-col gap-8 px-4 py-6 lg:flex">
      <Link to="/" className="self-start rounded-lg px-2 py-1">
        <Logo />
      </Link>
      <nav aria-label="Navigation principale" className="flex-1">
        <ul className="flex flex-col gap-1.5">
          {NAV.map(({ to, label, icon: Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  clsx(
                    'flex h-12 items-center gap-3 rounded-full px-4 text-[15px] font-bold transition-colors',
                    isActive
                      ? 'bg-accent-soft text-accent-ink'
                      : 'text-ink-2 hover:bg-surface-2 hover:text-ink',
                  )
                }
              >
                <Icon size={22} weight="duotone" aria-hidden="true" />
                {label}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
      <div className="flex flex-col gap-3">
        <CreditsCard />
        <div className="flex items-center justify-between gap-2 px-1">
          <p className="min-w-0 truncate text-sm font-bold text-ink">{me?.user.name ?? '…'}</p>
          <button
            type="button"
            onClick={logout}
            className="inline-flex items-center gap-1.5 rounded-full px-3 py-2 text-sm font-bold text-ink-2 hover:bg-surface-2 hover:text-ink"
          >
            <SignOut size={16} weight="bold" aria-hidden="true" />
            Sortir
          </button>
        </div>
      </div>
    </aside>
  );
}

/** Mobile : barre du haut (logo + crédits) et onglets en bas, toujours à portée de pouce. */
function MobileBars() {
  const { data: me } = useMe();
  return (
    <>
      <header className="sticky top-0 z-30 flex h-16 items-center justify-between bg-bg/90 px-4 backdrop-blur-md lg:hidden">
        <Link to="/" className="rounded-lg">
          <Logo size={28} />
        </Link>
        {me && (
          <Link
            to="/reglages"
            className="rounded-full bg-surface px-3.5 py-1.5 text-sm font-bold text-ink shadow-clay-sm tabular-nums"
          >
            {me.credits.balance} crédits
          </Link>
        )}
      </header>
      <nav
        aria-label="Navigation principale"
        data-tab-bar
        className="fixed inset-x-3 bottom-3 z-30 rounded-full bg-surface/95 p-1.5 shadow-clay-lg backdrop-blur-md lg:hidden"
      >
        <ul className="grid grid-cols-4">
          {NAV.map(({ to, short, icon: Icon, end }) => (
            <li key={to}>
              <NavLink
                to={to}
                end={end}
                className={({ isActive }) =>
                  clsx(
                    'flex h-14 flex-col items-center justify-center gap-0.5 rounded-full text-[12px] font-bold transition-colors',
                    isActive ? 'bg-accent-soft text-accent-ink' : 'text-ink-2',
                  )
                }
              >
                <Icon size={22} weight="duotone" aria-hidden="true" />
                {short}
              </NavLink>
            </li>
          ))}
        </ul>
      </nav>
    </>
  );
}

export function AppShell({ children }: { children?: ReactNode }) {
  const location = useLocation();
  // Revient en haut de page à chaque navigation (le contenu change entièrement).
  // biome-ignore lint/correctness/useExhaustiveDependencies: l'effet doit se déclencher à chaque changement de chemin
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [location.pathname]);

  return (
    <div className="min-h-dvh lg:grid lg:grid-cols-[260px_1fr]">
      <a
        href="#contenu"
        className="sr-only z-50 rounded-full bg-accent px-4 py-2 font-bold text-on-accent focus:not-sr-only focus:fixed focus:top-3 focus:left-3"
      >
        Aller au contenu
      </a>
      <SideNav />
      <MobileBars />
      <main id="contenu" className="mx-auto w-full max-w-[1120px] px-4 pt-4 pb-32 sm:px-8 lg:pt-10 lg:pb-16">
        {children ?? <Outlet />}
      </main>
    </div>
  );
}
